import { useCallback, useState } from 'react'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  getAzureDevOpsOrganizations,
  setAzureDevOpsOrganization
} from '~/services/organizationService'

interface OrgState {
  available: string[]
  selected: string
  showSelection: boolean
  isLoading: boolean
}

export function useAzureDevOpsOrganization() {
  const { currentWorkspace } = useWorkspaceContext()
  const [orgState, setOrgState] = useState<OrgState>({
    available: [],
    selected: '',
    showSelection: false,
    isLoading: false // Start with loading false, set to true only when actually loading
  })

  const loadOrganizations = useCallback(async () => {
    try {
      if (!currentWorkspace?.uuid) {
        console.error('Workspace UUID not available')
        return false
      }

      setOrgState(prev => ({ ...prev, isLoading: true }))

      const orgData = await getAzureDevOpsOrganizations(currentWorkspace.uuid)

      const shouldShowSelection =
        !orgData.selected_organization || orgData.available_organizations.length > 1

      setOrgState({
        available: orgData.available_organizations,
        selected: orgData.selected_organization || '',
        showSelection: shouldShowSelection,
        isLoading: false
      })

      return shouldShowSelection
    } catch (error) {
      console.error('useAzureDevOpsOrganization: Error loading organizations:', error)
      setOrgState(prev => ({ ...prev, isLoading: false }))
      return false
    }
  }, [currentWorkspace])

  const selectOrganization = useCallback(
    async (orgName: string) => {
      try {
        if (!currentWorkspace?.uuid) {
          console.error('Workspace UUID not available')
          return false
        }

        await setAzureDevOpsOrganization(orgName, currentWorkspace.uuid)
        setOrgState(prev => ({
          ...prev,
          selected: orgName,
          showSelection: false
        }))
        return true
      } catch (error) {
        console.error('Error selecting organization:', error)
        return false
      }
    },
    [currentWorkspace]
  )

  const showOrgSelector = useCallback(() => {
    setOrgState(prev => ({ ...prev, showSelection: true }))
  }, [])

  // Reset loading state for non-Azure DevOps connections
  const resetForNonAzure = useCallback(() => {
    setOrgState({
      available: [],
      selected: '',
      showSelection: false,
      isLoading: false
    })
  }, [])

  return {
    orgState,
    loadOrganizations,
    selectOrganization,
    showOrgSelector,
    resetForNonAzure
  }
}
